import Link from "next/link";
import React, { CSSProperties, useEffect, useRef, useState } from "react";

import {
  CartFillIcon,
  IconsProps,
  LocationOutlineIcon,
  LogoutOutlineIcon,
  ManageAddressOutlineIcon,
  MenuBarIcon,
  OrderListOutlineIcon,
  PhoneFillIcon,
  PhoneOutlineIcon,
  RightIcon,
  UserFillIcon,
  UserOutlineIcon,
  WhatsappOutlineIcon,
} from "@/components/Icons";
import MainLogo from "@/components/MainLogo";
import MenuBar, { DrawerMenu, MenuBarFull } from "@/components/MenuBar";
import SearchBar, {
  SearchBarMobile,
  SearchFullUI,
} from "@/components/SearchBar";
import { maxNavItem, maxNavItemMobile } from "@/settings";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fullName, toLoginPage } from "@/utils";
import { campaign, cartButtonId, onlineOnly, routes } from "@/utils/constants";
import { getPageRoute, getS3ImageUrl } from "@/utils/routes";
import { useRouter } from "@/hooks/useCompatRouter";
import useFetch from "@/hooks/useFetch";
import { getBuilderItem } from "@/requests/builder";
import classes from "./Header.module.css";

import appleStore from "@/assets/apple_store.png";
import appleStoreHover from "@/assets/apple_store_h.png";

import { logout } from "@/store/features/authSlice";
import Image from "next/image";

interface ButtonType {
  title: string;
  Icon: React.FC<IconsProps>;
  href: string;
  hasState: boolean;
  type:
    | "user"
    | "cart"
    | "wishlist"
    | "store"
    | "order"
    | "offers"
    | "phone"
    | "whatsapp";
}

const headerButtons: ButtonType[] = [
  //   {
  //     title: "Wishlist",
  //     Icon: FavouriteFillIcon,
  //     href: "#",
  //     hasState: true,
  //     type: 'wishlist',
  //   },
  {
    title: "Cart",
    Icon: CartFillIcon,
    href: "/cart",
    hasState: false,
    type: "cart",
  },
  {
    title: "User",
    Icon: UserFillIcon,
    href: "/user",
    hasState: false,
    type: "user",
  },
];

const footerButtons: ButtonType[] = [
  {
    title: "Cart",
    Icon: CartFillIcon,
    href: "/cart",
    hasState: false,
    type: "cart",
  },

  {
    title: "Request Order",
    Icon: OrderListOutlineIcon,
    href: "/request-order",
    hasState: false,
    type: "order",
  },
  {
    title: "User",
    Icon: UserOutlineIcon,
    href: "/user",
    hasState: false,
    type: "user",
  },
  {
    title: "Store Locator",
    Icon: LocationOutlineIcon,
    href: "/store-locator",
    hasState: false,
    type: "store",
  },
  // {
  // 	title: "Offers",
  // 	Icon: OfferOutlineIcon,
  // 	href: "/offers",
  // 	hasState: false,
  // 	type: "offers",
  // },
  // {
  //     title: "Call Us",
  //     Icon: PhoneOutlineIcon,
  //     href: "tel:",
  //     hasState: false,
  //     type: 'phone',
  // }
];

const rightButtons: ButtonType[] = [
  {
    title: "Call Us",
    Icon: PhoneOutlineIcon,
    href: "tel:",
    hasState: false,
    type: "phone",
  },
  {
    title: "Whatsapp",
    Icon: WhatsappOutlineIcon,
    href: "https://wa.me/8801318236991",
    hasState: false,
    type: "whatsapp",
  },
];

interface Props {
  className?: string;
  style?: CSSProperties;

  menuItems?: MenuItem[];
  navItems?: MenuItem[];
}

const Header = ({
  className,
  style,

  menuItems = [],
  navItems = [],
}: Props) => {
  const router = useRouter();

  const [searchOpen, setSearchOpen] = useState(false);

  const classNames = [classes.Header, className];

  return (
    <>
      <div className={classNames.join(" ")} style={style}>
        <HeaderTop
          menuItems={menuItems}
          navItems={navItems}
          onMobileSearchClick={() => setSearchOpen(true)}
        />
        <HeaderBottom menuItems={menuItems} navItems={navItems} />

        {/* <div className={`${classes.mobile} ${classes.nav}`}>
                    <div className="container">
                        <PageLinksSlide
                            links={navItems}
                            className={`${classes.pageLinks} ${classes.py}`}
                        />
                    </div>
                </div> */}
      </div>

      {router.pathname !== routes.checkout &&
        router.pathname !== routes.cart && <MobileFooter />}

      {searchOpen && <SearchFullUI onBack={() => setSearchOpen(false)} />}
    </>
  );
};

const HeaderTop = ({
  menuItems,
  navItems,
  onMobileSearchClick,
}: {
  menuItems?: MenuItem[];
  navItems?: MenuItem[];
  onMobileSearchClick?: () => void;
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const isLogged = useAppSelector((state) => !!state.auth.access_token);
  const cartCount = useAppSelector((state) => state.cart.count);
  const user = useAppSelector((state) => state.auth.user);
  const avatar = user?.avatar;
  const userName = user ? fullName(user.firstName, user.lastName) : null;

  const appleStoreWidget = useFetch(
    () => getBuilderItem("widget/storefront", "apple-store-locator"),
    { skip: false, deps: [] },
  );

  const appleBanner = appleStoreWidget.response?.settings?.banner;
  const appleMainImage = appleBanner?.mainImage?.image?.url;
  const appleHoverImage = appleBanner?.hoverImage?.image?.url;
  const appleLinkUrl =
    appleBanner?.link?.url ||
    "https://locate.apple.com/bd/en/sales?pt=all&lat=23.727846145629883&lon=90.40776062011719&address=Dhaka%2C+Bangladesh";

  const [menuOpen, setMenuOpen] = useState(false);

  const topRef = useRef<HTMLDivElement>(null);

  const rect = topRef.current?.getBoundingClientRect();

  const handleLogout = () => {
    dispatch(logout());
    router.reload();
  };

  return (
    <div className={classes.HeaderTop}>
      <div className={`${classes.contents} container`}>
        <button
          className={`${classes.menuButton}`}
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((isOpen) => !isOpen)}
        >
          <MenuBarIcon className={classes.icon} width={24} />
        </button>

        <div className={classes.left}>
          <MainLogo />
        </div>

        <div className={classes.center}>
          <SearchBar />
        </div>

        <div className={classes.right}>
          <div className="hidden md:block">
            <Link
              href={getPageRoute("page", "offers")}
              style={{ color: campaign.color }}
              className={`${classes.link} ${classes.offerBtn} !flex gap-0.5`}
            >
              <BlinkingThunder />
              <span>{campaign.title}</span>
            </Link>

            {/* <Link
            href={getPageRoute("custom", "/online-exclusive")}
            className={`gradient-text ${classes.link} ${classes.offerBtn}`}
          >
            {onlineOnly.title}
          </Link> */}
          </div>

          <div className={`${classes.mobile} ${classes.py} flex-1 ml-2`}>
            <SearchBarMobile onClick={onMobileSearchClick} />
          </div>
          <div className={classes.buttons}>
            <Link
              href={routes.storeLocator}
              className={
                classes.button +
                " hidden md:flex gap-1 md:gap-2 items-center md:!pr-3 !ml-0"
              }
            >
              <LocationOutlineIcon
                className={classes.outlineIcon}
                width={18}
                height={18}
              />
              <span className={classes.text}>Store Locator</span>
            </Link>

            {appleBanner && (
              <Link
                href={appleLinkUrl}
                target="_blank"
                className={
                  classes.appleStore +
                  " hidden lg:block gap-1 md:ml-2 lg:ml-3 relative"
                }
              >
                {appleMainImage ? (
                  <img
                    src={getS3ImageUrl(appleMainImage)}
                    alt="apple store"
                    className="w-auto h-[40px] min-w-[100px] object-contain"
                    style={{
                      aspectRatio: appleStore.width / appleStore.height,
                    }}
                  />
                ) : (
                  // <Image
                  //   src={appleStore}
                  //   alt="apple store"
                  //   unoptimized={true}
                  //   className="w-auto h-[40px] min-w-[100px] object-contain"
                  //   style={{
                  //     aspectRatio: appleStore.width / appleStore.height,
                  //   }}
                  // />
                  <></>
                )}

                {appleHoverImage ? (
                  <img
                    src={getS3ImageUrl(appleHoverImage)}
                    alt="apple store hover"
                    className="absolute left-0 top-0 z-10 w-full h-full object-contain opacity-0 hover:opacity-100 transition-opacity"
                    style={{
                      aspectRatio: appleStore.width / appleStore.height,
                    }}
                  />
                ) : (
                  // <Image
                  //   src={appleStoreHover}
                  //   alt="apple store"
                  //   unoptimized={true}
                  //   className="absolute left-0 top-0 z-10 w-full h-full object-contain opacity-0 hover:opacity-100 transition-opacity"
                  //   style={{
                  //     aspectRatio: appleStore.width / appleStore.height,
                  //   }}
                  // />
                  <></>
                )}
              </Link>
            )}

            {/*  find us on apple store  */}

            {headerButtons.map((btn, i) => {
              let counter = btn.type === "cart" ? cartCount : 0;

              const Button = (
                <button
                  // className={
                  // 	classes.button +
                  // 	(btn.type === "user" ||
                  // 	btn.type === "cart"
                  // 		? " hidden md:block"
                  // 		: "")
                  // }
                  className={`${classes.button} ${
                    btn.type === "user" || btn.type === "cart"
                      ? " !hidden md:!block"
                      : ""
                  } ${
                    (btn.type === "user" || btn.type === "cart") && classes.user
                  }`}
                  key={i}
                  id={btn.type === "cart" ? cartButtonId : undefined}
                  onClick={() => {
                    if (btn.type === "user" && !isLogged) {
                      toLoginPage(router);
                    }
                  }}
                >
                  {btn.type === "user" && !!avatar && (
                    <div className={classes.avatar}>
                      <Image
                        src={getS3ImageUrl(avatar)}
                        alt="avatar"
                        layout="fill"
                        objectFit="cover"
                        unoptimized={true}
                      />
                    </div>
                  )}
                  <btn.Icon
                    className={
                      classes.icon +
                      (btn.title.match(/track/i) ? " [&>*]:!fill-none" : " ")
                    }
                    width={14}
                  />
                  {counter > 0 && (
                    <span
                      className={`${classes.counter}${
                        btn.type === "user" ? " " + classes.dot : ""
                      }`}
                    >
                      {btn.type === "user" ? null : counter}
                    </span>
                  )}
                </button>
              );

              if (btn.hasState || (btn.type === "user" && !isLogged))
                return (
                  <div key={i} className={classes.parent}>
                    {Button}

                    {btn.type === "cart" && !!userName && (
                      <span className={`${classes.text} ${classes.name}`}>
                        Cart
                      </span>
                    )}
                  </div>
                );

              return (
                <div className={classes.parent + " relative"} key={i}>
                  <Link
                    className={" flex gap-3 items-center"}
                    href={btn.href || "#"}
                  >
                    {Button}

                    {btn.type === "user" && !!userName && (
                      <span className={`${classes.text} ${classes.name}`}>
                        {userName}
                      </span>
                    )}
                  </Link>
                  {isLogged && btn.type === "user" && (
                    <UserInfoCard
                      name={userName || ""}
                      isLoggedIn={isLogged}
                      onLogout={handleLogout}
                      className={classes.userInfoCard}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className={classes.mobileMenu}>
          <DrawerMenu
            menuData={navItems || []}
            onClose={() => setMenuOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

const HeaderBottom = ({
  menuItems,
  navItems,
}: {
  menuItems?: MenuItem[];
  navItems?: MenuItem[];
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={classes.HeaderBottom}
      onMouseLeave={() => setMenuOpen(false)}
    >
      <div className={`${classes.contents} container`}>
        {/* <div className={classes.left}>
          <button
            className={`${classes.button} ${classes.py}`}
            onMouseEnter={() => setMenuOpen(true)}
          >
            <MenuBarIcon className={classes.icon} width={14} />
            <Link href={routes.category}>
              <span className={classes.text}>All Categories</span>
            </Link>
          </button>

          <div className={classes.hr}></div>
        </div> */}

        <div
          className={classes.center + " relative"}
          onMouseEnter={() => setMenuOpen(false)}
        >
          <PageLinks
            links={navItems}
            className={`${classes.pageLinks} ${classes.py}`}
          />
        </div>

        <div className={classes.right}>
          {/* {phoneNumber && (
            <Link
              href={`tel:${phoneNumber}`}
              className={`${classes.button} ${classes.py}`}
            >
              <PhoneFillIcon width={18} height={18} className={classes.icon} />
              <span className={classes.text}>{phoneNumber}</span>
            </Link>
          )} */}

          <div className={`${classes.mobile} ${classes.py}`}>
            <SearchBarMobile />
          </div>
        </div>
      </div>

      {menuOpen && (
        <>
          <div className={classes.desktopMenu}>
            <MenuBarFull
              menuData={navItems || []}
              onClose={() => setMenuOpen(false)}
            />
          </div>
          <div className={classes.mobileMenu}>
            <DrawerMenu
              menuData={navItems || []}
              onClose={() => setMenuOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

const MobileFooter = () => {
  const router = useRouter();

  const isLogged = useAppSelector((state) => !!state.auth.access_token);
  const cartCount = useAppSelector((state) => state.cart.count);
  const phoneNumber = useAppSelector(
    (state) => state.global.socialLinksWidget?.phone || "",
  );

  return (
    <div className={`${classes.MobileFooter} ${classes.mobile}`}>
      <div className={classes.buttons}>
        {footerButtons.map((btn, i) => {
          let counter = btn.type === "cart" ? cartCount : 0;

          return (
            <button
              className={classes.button}
              key={i}
              onClick={() => {
                if (btn.type === "user" && !isLogged) {
                  toLoginPage(router);
                } else if (btn.hasState === false && btn.href !== "#") {
                  router.push(
                    btn.href + (btn.type === "phone" ? phoneNumber : ""),
                  );
                }
              }}
            >
              <btn.Icon
                className={classes.icon + " [&>*]:!fill-none"}
                width={20}
                height={20}
              />
              {/* {counter > 0 && (
								<span
									className={`${classes.counter}${
										btn.type === "user"
											? " " + classes.dot
											: ""
									}`}>
									{btn.type === "user" ? null : counter}
								</span>
							)} */}

              <span className={classes.text}>{btn.title}</span>
            </button>
          );
        })}
      </div>

      <div className={classes.rightButtons}>
        {rightButtons.map((btn, i) => {
          return (
            <button
              className={classes.button}
              title={btn.title}
              key={i}
              onClick={() => {
                if (btn.type === "whatsapp") {
                  window.open(btn.href, "_blank");
                } else {
                  router.push(
                    btn.href + (btn.type === "phone" ? phoneNumber : ""),
                  );
                }
              }}
            >
              <btn.Icon
                className={
                  classes.icon +
                  (btn.title.match(/track/i) ? " [&>*]:!fill-none" : " ")
                }
                width={btn.type === "whatsapp" ? 16 : 14}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface LinksProps {
  mainMenuOpen?: boolean;
  isMobile?: boolean;
  closeMainMenu?: () => void;
  className?: string;
  links?: MenuItem[];
}

const PageLinks = ({
  mainMenuOpen = false,
  isMobile = false,
  closeMainMenu = () => {},
  className,
  links = [],
}: LinksProps) => {
  const [dropdownActive, setDropdownActive] = useState(false);
  const [translateX, setTranslateX] = useState<number>();
  const [menuIndex, setMenuIndex] = useState<number | null>(null);

  const ref = React.useRef<HTMLUListElement>(null);

  const safeLinks = Array.isArray(links) ? links : [];

  useEffect(() => {
    if (mainMenuOpen) {
      setDropdownActive(false);
    }
  }, [mainMenuOpen]);

  const _maxNavItem = !isMobile ? maxNavItem : maxNavItemMobile;

  return (
    <div
      className="relative w-full"
      onMouseLeave={() => setDropdownActive(false)}
    >
      <ul
        ref={ref}
        className={`${classes.PageLinks} ${className} hide-scrollbar`}
      >
        {safeLinks.map((link, i) => {
          const url = getPageRoute(link.type as any, link.url);

          return (
            <Link
              key={i}
              // href={url}
              href={url}
              className={
                classes.link + (dropdownActive ? " " + classes.active : "")
              }
              onMouseEnter={(e) => {
                if (link.submenu && link.submenu.length > 0) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTranslateX(rect.x);

                  setMenuIndex(i);
                  setDropdownActive(true);
                  closeMainMenu();
                } else {
                  setDropdownActive(false);
                }
              }}
            >
              {link.name}
            </Link>
          );
        })}

        {safeLinks.length === 0 && (
          <a className={classes.link + " opacity-0"}>I</a>
        )}
      </ul>
      {dropdownActive && (
        <MenuBar
          menuData={
            menuIndex !== null
              ? (safeLinks[menuIndex].submenu ?? [])
              : safeLinks.slice(_maxNavItem)
          }
          onClose={() => setDropdownActive(false)}
          dropdown={true}
          translateX={translateX}
          submenuSide={
            menuIndex !== null && menuIndex < Math.ceil(safeLinks.length / 2)
              ? "right"
              : "left"
          }
        />
      )}

      {safeLinks.length > 5 && (
        <button
          className={classes.rightArrow}
          onClick={() => {
            if (ref.current) {
              ref.current.scrollTo({
                left: ref.current.scrollLeft + 100,
                behavior: "smooth",
              });
            }
          }}
        >
          <RightIcon width={18} height={18} className={classes.icon} />
        </button>
      )}
    </div>
  );
};

type UserInfoTypes = "NOTIFICATION" | "UNKNOWN" | "AFFILIATATION";

const userNavItems: {
  Icon: React.FC<IconsProps>;
  title: string;
  href: string;
  type: UserInfoTypes;
}[] = [
  {
    Icon: UserOutlineIcon,
    title: "My Profile",
    href: routes.user,
    type: "UNKNOWN",
  },
  {
    Icon: OrderListOutlineIcon,
    title: "My Orders",
    href: routes.orders,
    type: "UNKNOWN",
  },
  {
    Icon: ManageAddressOutlineIcon,
    title: "Manage Address",
    href: routes.address,
    type: "UNKNOWN",
  },
  // {
  //     Icon: UserOutlineIcon,
  //     title: 'My Account',
  //     href: memberSubRoutePaths.profile,
  //     type: 'UNKNOWN',
  // },
];

interface UserInfo {
  name: string;
  avatar: string;
  className?: string;
}

interface UserInfoCardProps {
  name: string;
  onLogout: () => void;
  notifictionCount?: number;
  isLoggedIn?: boolean;
  affiliation?: boolean;
  className?: string;
}

const UserInfoCard = ({
  name,
  onLogout,
  isLoggedIn = false,
  notifictionCount = 0,
  affiliation = false,
  className,
}: UserInfoCardProps) => {
  const router = useRouter();

  const onLoginOrLogout = () => {
    if (isLoggedIn) {
      onLogout();
    } else {
      toLoginPage(router);
    }
  };

  return (
    <div className={`${classes.UserInfoCard} ${className}`}>
      {isLoggedIn && (
        <>
          <Link href={routes.user} passHref>
            <button className={classes.titleBtn}>{name}</button>
          </Link>

          {userNavItems.map((item, index) =>
            item.type !== "AFFILIATATION" || affiliation ? (
              <Link key={index} href={item.href || "#"}>
                <div className={classes.item}>
                  <div className={classes.logo}>
                    <item.Icon
                      className={classes.icon}
                      width={24}
                      height={24}
                    />

                    {item.type === "NOTIFICATION" && notifictionCount > 0 && (
                      <span className={classes.dot}></span>
                    )}
                  </div>

                  <p className={classes.title}>{item.title}</p>
                </div>
              </Link>
            ) : null,
          )}
        </>
      )}

      <button onClick={onLoginOrLogout} className={classes.item}>
        <div className={classes.logo}>
          <LogoutOutlineIcon className={classes.icon} width={24} height={24} />
        </div>

        <p className={classes.title}>{isLoggedIn ? "Logout" : "Login"}</p>
      </button>
    </div>
  );
};

const PageLinksSlide = ({
  mainMenuOpen = false,
  isMobile = false,
  closeMainMenu = () => {},
  className,
  links = [],
}: LinksProps) => {
  const ref = React.useRef<HTMLUListElement>(null);

  const safeLinks = Array.isArray(links) ? links : [];

  return (
    <ul
      ref={ref}
      className={`${classes.PageLinksSlide} ${className} hide-scrollbar`}
    >
      <div className={classes.links}>
        {safeLinks.map((link, i) => {
          const url = getPageRoute(link.type as any, link.url);

          return (
            <Link key={i} href={url} className={classes.link}>
              {link.name}
            </Link>
          );
        })}
      </div>

      {safeLinks.length > 2 && (
        <button
          className={classes.button}
          onClick={() => {
            if (ref.current) {
              ref.current.scrollTo({
                left: ref.current.scrollLeft + 100,
                behavior: "smooth",
              });
            }
          }}
        >
          <RightIcon width={18} height={18} />
        </button>
      )}
    </ul>
  );
};

export default Header;

export const BlinkingMoon = () => {
  return (
    <svg
      width="24"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-primary animate-blink"
    >
      <path
        d="M13.3986 7.64605C13.495 7.37724 13.88 7.37724 13.9764 7.64605L14.2401 8.38111C14.271 8.46715 14.3395 8.53484 14.4266 8.56533L15.1709 8.82579C15.443 8.92103 15.443 9.30119 15.1709 9.39644L14.4266 9.65689C14.3395 9.68738 14.271 9.75507 14.2401 9.84112L13.9764 10.5762C13.88 10.845 13.495 10.845 13.3986 10.5762L13.1349 9.84112C13.104 9.75507 13.0355 9.68738 12.9484 9.65689L12.2041 9.39644C11.932 9.30119 11.932 8.92103 12.2041 8.82579L12.9484 8.56533C13.0355 8.53484 13.104 8.46715 13.1349 8.38111L13.3986 7.64605Z"
        fill="currentColor"
      />
      <path
        d="M16.3074 10.9122C16.3717 10.733 16.6283 10.733 16.6926 10.9122L16.8684 11.4022C16.889 11.4596 16.9347 11.5047 16.9928 11.525L17.4889 11.6987C17.6704 11.7622 17.6704 12.0156 17.4889 12.0791L16.9928 12.2527C16.9347 12.2731 16.889 12.3182 16.8684 12.3756L16.6926 12.8656C16.6283 13.0448 16.3717 13.0448 16.3074 12.8656L16.1316 12.3756C16.111 12.3182 16.0653 12.2731 16.0072 12.2527L15.5111 12.0791C15.3296 12.0156 15.3296 11.7622 15.5111 11.6987L16.0072 11.525C16.0653 11.5047 16.111 11.4596 16.1316 11.4022L16.3074 10.9122Z"
        fill="currentColor"
      />
      <path
        d="M17.7693 3.29184C17.9089 2.90272 18.4661 2.90272 18.6057 3.29184L19.0842 4.62551C19.1288 4.75006 19.2281 4.84805 19.3542 4.89219L20.7045 5.36475C21.0985 5.50263 21.0985 6.05293 20.7045 6.19081L19.3542 6.66337C19.2281 6.7075 19.1288 6.80549 19.0842 6.93005L18.6057 8.26372C18.4661 8.65284 17.9089 8.65284 17.7693 8.26372L17.2908 6.93005C17.2462 6.80549 17.1469 6.7075 17.0208 6.66337L15.6705 6.19081C15.2765 6.05293 15.2765 5.50263 15.6705 5.36475L17.0208 4.89219C17.1469 4.84805 17.2462 4.75006 17.2908 4.62551L17.7693 3.29184Z"
        fill="currentColor"
      />
      <path
        d="M3 13.4597C3 17.6241 6.4742 21 10.7598 21C14.0591 21 16.8774 18.9993 18 16.1783C17.1109 16.5841 16.1181 16.8109 15.0709 16.8109C11.2614 16.8109 8.17323 13.8101 8.17323 10.1084C8.17323 8.56025 8.71338 7.13471 9.62054 6C5.87502 6.5355 3 9.67132 3 13.4597Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const BlinkingThunder = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="-6 -2 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-primary animate-blink"
    >
      <path
        d="M8.997 6.968H6.708V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v5.923a1 1 0 0 0 .966 1l1.937.061v7.404a.549.549 0 0 0 1.053.216l3.96-9.242a1 1 0 0 0-.92-1.394zM8.708 3v1.968h.289a3 3 0 0 1 2.757 4.181l-3.96 9.243a2.549 2.549 0 0 1-4.891-1.004v-5.466A3 3 0 0 1 0 8.923V3a3 3 0 0 1 3-3h2.708a3 3 0 0 1 3 3z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
